/**
 * Apply Babylon Sandbox camera defaults to an ArcRotateCamera.
 *
 * Based on the battle-tested defaults from the official Babylon.js Sandbox
 * (packages/tools/sandbox/src/components/renderingZone.tsx).
 */

import type { ArcRotateCamera, FramingBehavior, Scene } from "@babylonjs/core";

interface SandboxCameraOptions {
  /** Skip useFramingBehavior (for cameras with fixed setups like physics arenas) */
  skipFramingBehavior?: boolean;
  /** Enable per-frame dynamic sensitivity (panningSensibility & speed scale with radius) */
  dynamicSensitivity?: boolean;
}

export function applySandboxCameraDefaults(
  camera: ArcRotateCamera,
  scene: Scene,
  options?: SandboxCameraOptions,
): void {
  const skipFraming = options?.skipFramingBehavior ?? false;
  const dynamicSensitivity = options?.dynamicSensitivity ?? true;

  // --- Framing behavior (sandbox: renderingZone.tsx:207-221) ---
  if (!skipFraming) {
    camera.useFramingBehavior = true;
    const framingBehavior = camera.getBehaviorByName(
      "Framing",
    ) as FramingBehavior | null;
    if (framingBehavior) {
      framingBehavior.framingTime = 0;
      framingBehavior.elevationReturnTime = -1;
    }
    // Zoom to scene bounds
    if (scene.meshes.length) {
      camera.lowerRadiusLimit = null;
      const worldExtends = scene.getWorldExtends(
        (mesh) => mesh.isVisible && mesh.isEnabled(),
      );
      framingBehavior?.zoomOnBoundingInfo(worldExtends.min, worldExtends.max);
    }
    camera.upperRadiusLimit = 5 * camera.radius;
  }

  // --- Zoom/pinch controls (sandbox: renderingZone.tsx:227-231) ---
  camera.wheelDeltaPercentage = 0.01;
  camera.pinchDeltaPercentage = 0.01;
  camera.pinchPrecision = 200 / camera.radius;

  // --- Dynamic sensitivity per frame (sandbox: renderingZone.tsx:318-325) ---
  if (dynamicSensitivity) {
    scene.registerBeforeRender(() => {
      camera.panningSensibility = 5000 / camera.radius;
      camera.speed = camera.radius * 0.2;
    });
  }
}
